/**
 * Permissions Matrix Component
 *
 * A permissions matrix table showing permissions by role,
 * inspired by Slack's permissions page. Content is driven by Statsig Dynamic Config.
 */

import { useRef } from 'react';
import { useTableScrollIndicators } from '@hooks/useTableScrollIndicators';
import { Check, HourglassEmpty, PersonOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { usePermissionsMatrixConfig } from '../store';

/**
 * Get the icon component for a permission status
 */
function getStatusIcon(status: 'default' | 'configurable' | 'coming_soon' | 'none') {
  switch (status) {
    case 'default':
      return <Check sx={{ fontSize: '1.25rem', color: 'success.main' }} />;
    case 'configurable':
      return <PersonOutlined sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />;
    case 'coming_soon':
      return <HourglassEmpty sx={{ fontSize: '1.25rem', color: 'warning.main' }} />;
    case 'none':
      return null;
    default:
      return null;
  }
}

/**
 * Legend component showing what each icon means - stacked vertically
 */
interface PermissionsMatrixProps {
  title?: string;
  onClose?: () => void;
}

function PermissionsLegend({
  legend,
}: {
  legend: { default: string; configurable: string; coming_soon: string };
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Check sx={{ fontSize: '1.1rem', color: 'success.main' }} />
        <Typography variant="body2" color="text.secondary">
          {legend.default}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonOutlined sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {legend.configurable}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HourglassEmpty sx={{ fontSize: '1.1rem', color: 'warning.main' }} />
        <Typography variant="body2" color="text.secondary">
          {legend.coming_soon}
        </Typography>
      </Box>
    </Box>
  );
}

export function PermissionsMatrix({ title, onClose }: PermissionsMatrixProps = {}) {
  const theme = useTheme();
  const config = usePermissionsMatrixConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const { showRightIndicator, showStickyColumnShadow } = useTableScrollIndicators(containerRef);

  return (
    <Card
      elevation={1}
      sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'grey.200' }}
    >
      {/* Title */}
      <CardHeader
        title={title ?? config.title}
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        action={
          onClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          )
        }
      />

      <CardContent sx={{ py: 0 }}>
        {/* Subtitle */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {config.subtitle}
        </Typography>

        {/* Notes */}
        {config.notes && config.notes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {config.notes.map((note, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                {note}
              </Typography>
            ))}
          </Box>
        )}

        {/* Legend */}
        <PermissionsLegend legend={config.legend} />
      </CardContent>

      {/* Permissions Table */}
      <CardContent sx={{ position: 'relative', p: 0, '&:last-child': { pb: 0 } }}>
        <TableContainer
          ref={containerRef}
          sx={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
              borderRadius: 4,
            },
          }}
        >
          <Table
            size="medium"
            sx={{
              minWidth: 650,
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              <col style={{ width: '25%' }} />
              {config.roles.map(role => (
                <col key={role.id} style={{ width: `${75 / config.roles.length}%` }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'grey.50',
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                  borderTop: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                {/* Empty cell for permission column header */}
                <TableCell
                  sx={{
                    bgcolor: 'grey.50',
                    border: 'none',
                    py: 1.5,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    '&::after': showStickyColumnShadow
                      ? {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '1px',
                          backgroundColor: theme.palette.grey[200],
                        }
                      : {},
                  }}
                />
                {/* Role headers - horizontal on wide screens, rotated on narrow */}
                {config.roles.map(role => (
                  <TableCell
                    key={role.id}
                    align="center"
                    sx={{
                      border: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      py: 1.5,
                      px: 1,
                      // On narrow screens, rotate the text
                      [theme.breakpoints.down('md')]: {
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)',
                        whiteSpace: 'nowrap',
                        height: 'auto',
                      },
                      // On wide screens, allow text to wrap
                      [theme.breakpoints.up('md')]: {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        verticalAlign: 'bottom',
                      },
                    }}
                  >
                    {role.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {config.permissions.map((permission, rowIndex) => (
                <TableRow
                  key={permission.id}
                  sx={{
                    bgcolor: rowIndex % 2 === 1 ? 'grey.50' : 'transparent',
                    '&:not(:last-child)': {
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    },
                  }}
                >
                  {/* Permission name */}
                  <TableCell
                    sx={{
                      bgcolor: rowIndex % 2 === 1 ? 'grey.50' : 'background.paper',
                      border: 'none',
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      py: 1.5,
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      '&::after': showStickyColumnShadow
                        ? {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '1px',
                            backgroundColor: theme.palette.grey[200],
                          }
                        : {},
                    }}
                  >
                    {permission.label}
                  </TableCell>
                  {/* Status cells for each role */}
                  {config.roles.map(role => {
                    const status = permission.statuses[role.id] || 'none';
                    return (
                      <TableCell
                        key={`${permission.id}-${role.id}`}
                        align="center"
                        sx={{
                          border: 'none',
                          py: 1.5,
                          px: 1,
                        }}
                      >
                        {getStatusIcon(status)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Right fade gradient when scrollable */}
        {showRightIndicator && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: 32,
              pointerEvents: 'none',
              background: theme =>
                `linear-gradient(to left, ${theme.palette.grey[200]}, transparent)`,
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
